import { sumBy } from '@/utils/aggregate';

export type RedeemableHolding = { quantity: number; unit_price: number; current_value: number };

/** Sell % is persisted as SellOrder.condition_1/2 — DecimalField(decimal_places=2). */
const PCT_SCALE = 100;
const MAX_PCT_UNITS = 100 * PCT_SCALE;

/**
 * Value raised by selling `pct`% of a holding. Quantity rounds to whole shares,
 * mirroring SellOrderCalculator for SELL_PCT_OF_HOLDINGS.
 */
export const sellValueAtPct = (holding: RedeemableHolding, pct: number): number =>
    Math.round(holding.quantity * pct / 100) * holding.unit_price;

/** Total value raised by selling the same `pct`% of every holding. */
export const totalSellValueAtPct = (holdings: RedeemableHolding[], pct: number): number =>
    sumBy(holdings, h => sellValueAtPct(h, pct));

/**
 * The single sell % to apply to every holding so a redemption raises `amount`.
 *
 * Selling the same fraction of every position shrinks each one proportionally,
 * so every stock keeps its weight in the portfolio after the redemption.
 *
 * The exact fraction is `amount / portfolio value`, but the backend stores the
 * % at 2 decimals and rounds each order to whole shares — so this returns the
 * first 2-decimal % at or above that fraction whose rounded proceeds cover
 * `amount`. It never goes below the exact fraction, even where rounding would
 * let a lower % cover the amount.
 *
 * Returns null when `amount` is not positive or exceeds the portfolio value.
 */
export const proportionalSellPct = (holdings: RedeemableHolding[], amount: number): number | null => {
    const portfolioValue = sumBy(holdings, 'current_value');
    if (!(amount > 0) || portfolioValue <= 0 || amount > portfolioValue) return null;

    // The epsilon keeps float noise (e.g. 10.000000000002) from bumping an exact % up a step.
    let units = Math.ceil(amount / portfolioValue * MAX_PCT_UNITS - 1e-9);
    while (units < MAX_PCT_UNITS && totalSellValueAtPct(holdings, units / PCT_SCALE) < amount) {
        units++;
    }
    return Math.min(units, MAX_PCT_UNITS) / PCT_SCALE;
};

/**
 * Split a redemption between the client's available cash and stock sales.
 * Cash pays out first, so only the shortfall is raised by selling holdings.
 */
export const splitRedemptionAmount = (amount: number, availableCash: number): { fromCash: number; fromStocks: number } => {
    const fromCash = Math.min(amount, Math.max(availableCash, 0));
    return { fromCash, fromStocks: amount - fromCash };
};

/**
 * Cash a redemption may use: available cash less a buffer of `bufferPct`% of the
 * holdings value, kept back in the account. Floored at 0.
 */
export const usableRedemptionCash = (availableCash: number, holdingsValue: number, bufferPct: number): number => {
    const buffer = bufferPct > 0 ? bufferPct / 100 * holdingsValue : 0;
    return Math.max(availableCash - buffer, 0);
};
